import {
    IROIWidgetConfig,
    IROICalculationMethod,
    IROITimeframe,
    IROICurrency,
    ITaskConfig,
    IActivityConfig,
} from "./models/roi-widget.props";

export const INITIAL_VALUES: IROIWidgetConfig = {
    title: "Gluware ROI Metrics",
    metric: {value: "cost_savings", label: "Cost Savings"},
    visualizationMode: {value: "numeric", label: "Numeric indicator"},
    compareTo: {
        enabled: false,
        option: {value: "previous_period", label: "Previous period"},
    },
    reportFormat: {value: "pdf", label: "PDF"},
    globalSettings: {
        averageHourlyRate: 0,
        currency: {value: "USD", label: "USD ($)"},
    },
    taskConfiguration: [],
};

// Metric Options
export const METRIC_OPTIONS = [
    {value: "cost_savings", label: "Cost savings"},
    {value: "tasks_automated", label: "Tasks automated"},
    {value: "time_saved", label: "Time saved through automation"},
    {value: "top_automated_tasks", label: "Top automated tasks"},
];

// Visualization Mode Options
export const VISUALIZATION_OPTIONS = [
    {value: "numeric", label: "Numeric indicator"},
    {value: "bar_chart", label: "Bar chart"},
    {value: "line_chart", label: "Line chart"},
];

// Compare To Options
export const COMPARE_TO_OPTIONS = [
    {value: "previous_period", label: "Previous period"},
    {value: "same_period_last_year", label: "Same period last year"},
];

// Report Format Options
export const REPORT_FORMAT_OPTIONS = [
    {value: "pdf", label: "PDF"},
    {value: "csv", label: "CSV"},
];

// Currency Options - ordered by priority (USD, EUR, GBP first)
export const CURRENCY_OPTIONS = [
    {value: "USD", label: "USD ($)", symbol: "$"},
    {value: "EUR", label: "EUR (€)", symbol: "€"},
    {value: "GBP", label: "GBP (£)", symbol: "£"},
    {value: "JPY", label: "JPY (¥)", symbol: "¥"},
    {value: "CAD", label: "CAD (C$)", symbol: "C$"},
    {value: "AUD", label: "AUD (A$)", symbol: "A$"},
    {value: "CHF", label: "CHF (Fr)", symbol: "Fr"},
    {value: "CNY", label: "CNY (¥)", symbol: "¥"},
    {value: "INR", label: "INR (₹)", symbol: "₹"},
];

// Currency symbol mapping for easy lookup
export const CURRENCY_SYMBOLS: {[key: string]: string} = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    CAD: "C$",
    AUD: "A$",
    CHF: "Fr",
    CNY: "¥",
    INR: "₹",
};

// Utility function to format currency values with symbols
export const formatCurrency = (amount: number, currencyCode: string): string => {
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;

    // Format number with commas for thousands
    const formattedAmount = amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });

    // For currencies where symbol comes after (like kr for SEK/NOK/DKK)
    const suffixCurrencies = ["SEK", "NOK", "DKK"];
    if (suffixCurrencies.includes(currencyCode)) {
        return `${formattedAmount} ${symbol}`;
    }

    // For most currencies, symbol comes before
    return `${symbol}${formattedAmount}`;
};

// Activity Types (matches backend design)
export const ACTIVITY_TYPES = {
    CAPTURE: "capture", // Device Discovery (Config Captures)
    BACKUP: "backup", // Backup Management (Config Backup)
    AUDIT: "audit", // Compliance Checks (Config Audit)
    OS_UPGRADE: "osUpgrade", // Online/Offline OS Upgrades
    FEATURE_PROVISIONING: "featureProvisioning", // Config Deployment (Provisioning)
    INVENTORY_REPORT: "inventoryReport", // Inventory Reports (Data Explorer Reports)
    WORKFLOW_PREFIX: "Workflow:", // RPA Workflow Executions (Workflow:<workflowTypeName>:<workflowTypeId>)
};

// Default Task Types (for UI display)
export const DEFAULT_TASK_TYPES: ITaskConfig[] = [
    {
        id: ACTIVITY_TYPES.CAPTURE,
        name: "Device Discovery",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
    {
        id: ACTIVITY_TYPES.FEATURE_PROVISIONING,
        name: "Config Deployment",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
    {
        id: ACTIVITY_TYPES.OS_UPGRADE,
        name: "OS Updates",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
    {
        id: ACTIVITY_TYPES.AUDIT,
        name: "Compliance Check",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
    {
        id: ACTIVITY_TYPES.BACKUP,
        name: "Backup Management",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
    {
        id: ACTIVITY_TYPES.INVENTORY_REPORT,
        name: "Inventory Reports",
        enabled: false,
        timePerTask: 0,
        unit: "hours",
    },
];
